/**
 * TPay installment channels (TRE-1691).
 *
 * An installment transaction is an ordinary one-time TPay payment — the merchant
 * is paid in full immediately and the bank carries the credit. It differs from
 * `price.installments`, which counts Stripe subscription installments; the two
 * must never be conflated.
 *
 * A product may expose several channels at once; they differ in interest rate and
 * number of installments, so the checkout lets the customer pick one and links to
 * the matching TPay calculator.
 *
 * Channels have to be activated per POS on the TPay side (a separate agreement
 * with the bank); this list only describes what TPay can accept.
 *
 * @see https://docs-api.tpay.com/pl/payment-methods/installments/
 */
export interface TPayInstallmentChannel {
  channelId: number;
  /**
   * Internal name, matching the channel list in the TPay panel — it is what we
   * reconcile commission against, so it has to stay recognisable there even when
   * two channels look identical to a customer. NEVER render this in the checkout.
   */
  label: string;
  /**
   * Default name shown to the customer. Carries only what they are buying (bank,
   * number of installments, their interest rate) — never our commission. A product
   * can override it per channel via `tpayInstallmentsChannelLabels`.
   */
  customerLabel: string;
  /**
   * TPay's embeddable rate calculator. Fixed pixel dimensions and no amount
   * parameter — the widget is a standalone simulator, not a quote for this order.
   * PragmaPay has none.
   */
  calculator?: { url: string; width: number; height: number };
  /**
   * The bank's own bound, on top of the range configured for the product. Either
   * side may be absent — we only enforce what is actually known, because a made-up
   * bound hides installments for orders the bank would have taken (TRE-1692).
   *
   * PragmaPay's comes from TPay's docs; Pekao's and Alior's were read off the
   * sliders in their calculators, which is the closest thing to a source there is —
   * TPay publishes no per-bank limits.
   */
  documentedRange?: { minAmount?: number; maxAmount?: number };
}

const PEKAO_CALCULATOR = (channelId: number) => ({
  url: `https://secure.tpay.com/installments/calculator?channelId=${channelId}`,
  width: 680,
  height: 680,
});

/** All four Pekao calculators drive their amount slider from 100 to 20 000 zł. */
const PEKAO_RANGE = { minAmount: 10_000, maxAmount: 2_000_000 };

/**
 * Alior's calculator opens its "Wartość koszyka" slider at 300 zł. Its upper stop
 * (9 259 zł) is deliberately NOT enforced — the number looks derived from a maximum
 * total credit rather than being a product limit, and capping on a guess would hide
 * installments for orders the bank would have financed.
 */
const ALIOR_RANGE = { minAmount: 30_000 };

export const TPAY_INSTALLMENT_CHANNELS: TPayInstallmentChannel[] = [
  {
    channelId: 77,
    label: "Raty Pekao (4–36 rat)",
    customerLabel: "Raty Pekao",
    calculator: PEKAO_CALCULATOR(77),
    documentedRange: PEKAO_RANGE,
  },
  {
    channelId: 78,
    label: "Raty Pekao 3 x 0%",
    customerLabel: "Raty Pekao 3 × 0%",
    calculator: PEKAO_CALCULATOR(78),
    documentedRange: PEKAO_RANGE,
  },
  {
    channelId: 80,
    label: "Raty Pekao do 10 x 0%",
    customerLabel: "Raty Pekao do 10 × 0%",
    calculator: PEKAO_CALCULATOR(80),
    documentedRange: PEKAO_RANGE,
  },
  {
    channelId: 81,
    label: "Raty Pekao do 50 mies.",
    customerLabel: "Raty Pekao do 50 rat",
    calculator: PEKAO_CALCULATOR(81),
    documentedRange: PEKAO_RANGE,
  },
  {
    channelId: 49,
    label: "Alior Raty",
    customerLabel: "Alior Raty",
    calculator: { url: "https://secure.tpay.com/alior-raty/?range&logo", width: 680, height: 600 },
    documentedRange: ALIOR_RANGE,
  },
  {
    channelId: 86,
    label: "PragmaPay biznes",
    customerLabel: "PragmaPay",
    documentedRange: { minAmount: 10_000, maxAmount: 5_000_000 },
  },
];

/**
 * What the checkout shows for a channel: the product's own wording when set,
 * otherwise the channel's default. Falls back to the channel id rather than to
 * `label`, so a bad key can never leak an internal, commission-bearing name.
 */
export const getTPayInstallmentChannelLabel = (
  channel: Pick<TPayInstallmentChannel, "channelId" | "customerLabel">,
  labels?: Record<string, string> | null,
): string => labels?.[String(channel.channelId)]?.trim() || channel.customerLabel;

export const TPAY_INSTALLMENT_CHANNEL_IDS = TPAY_INSTALLMENT_CHANNELS.map((channel) => channel.channelId);

/** 1000 zł — installments below this are rarely worth the bank's paperwork. */
export const DEFAULT_TPAY_INSTALLMENTS_MIN_AMOUNT = 100_000;

/**
 * The product's installments configuration. Amounts are in the minor unit
 * (grosze), matching `unit_amount`.
 */
export interface TPayInstallmentsRange {
  channelIds?: number[] | null;
  minAmount?: number | null;
  maxAmount?: number | null;
}

export const getTPayInstallmentChannel = (channelId?: number | null): TPayInstallmentChannel | null =>
  TPAY_INSTALLMENT_CHANNELS.find((channel) => channel.channelId === channelId) ?? null;

/**
 * Which of the product's channels can carry `amount` (minor unit, after
 * discounts). A channel qualifies when the amount sits inside the range
 * configured for the product and — where TPay documents one — inside the bank's
 * own limits too.
 *
 * Returned in `TPAY_INSTALLMENT_CHANNELS` order so the checkout's default pick
 * and the server's fallback pick agree.
 */
export const getEligibleTPayInstallmentChannels = (
  amount: number,
  range: TPayInstallmentsRange,
): TPayInstallmentChannel[] => {
  const minAmount = range.minAmount ?? DEFAULT_TPAY_INSTALLMENTS_MIN_AMOUNT;
  const maxAmount = range.maxAmount ?? Infinity;

  if (amount < minAmount || amount > maxAmount) {
    return [];
  }

  return TPAY_INSTALLMENT_CHANNELS.filter(
    (channel) =>
      range.channelIds?.includes(channel.channelId) &&
      amount >= (channel.documentedRange?.minAmount ?? 0) &&
      amount <= (channel.documentedRange?.maxAmount ?? Infinity),
  );
};

/** Whether `amount` can be paid in installments on any of the product's channels. */
export const isAmountEligibleForTPayInstallments = (amount: number, range: TPayInstallmentsRange): boolean =>
  getEligibleTPayInstallmentChannels(amount, range).length > 0;
